import { ProfileAutosuggest } from '@seek/profile-edit-ui';
import { useTranslations } from '@vocab/react';
import { useCallback, useEffect, useRef } from 'react';
import { useController, type Control } from 'react-hook-form';

import { useConfig } from 'src/context/config';
import type { Zone } from 'src/types';

import translations from './PersonalDetails.vocab';
import type { DefaultValues } from './types';

interface HomeLocationFieldProps {
  control: Control<DefaultValues>;
  onFocus: () => void;
}

type LocationType = 'Home' | 'Preferred';

interface ProfileLocationProps {
  zone: Zone;
  locationType: LocationType;
}

const HomeLocationField: React.FC<HomeLocationFieldProps> = ({
  control,
  onFocus,
}) => {
  const { t } = useTranslations(translations);
  const { config } = useConfig();
  const { ZONE: zone } = config;

  const { field: homeLocation, fieldState: homeLocationState } = useController({
    name: 'homeLocation',
    control,
  });

  const listenToBlur = useRef<HTMLInputElement>(
    document.createElement('input'),
  );

  const handleBlur = useCallback(() => {
    homeLocation.onBlur();
  }, [homeLocation]);

  useEffect(() => {
    const node = listenToBlur.current;

    if (node) {
      node.addEventListener('blur', handleBlur);
      node.addEventListener('focus', onFocus);
    }

    return () => {
      if (node) {
        node.removeEventListener('blur', handleBlur);
        node.removeEventListener('focus', onFocus);
      }
    };
  }, [handleBlur, onFocus]);

  const hasError = homeLocationState.invalid;

  return (
    <ProfileAutosuggest
      label={t('Home location')}
      id="currentLocation2"
      asset="ProfileLocation"
      profileLocationProps={
        {
          zone,
          locationType: 'Home',
        } as ProfileLocationProps
      }
      showNoResultsMessage={true}
      data={{ automation: 'current-location2' }}
      inputRef={listenToBlur}
      tone={hasError ? 'critical' : undefined}
      value={homeLocation.value}
      onChange={homeLocation.onChange}
      message={homeLocationState.error?.message}
      scrollToTopOnMobile={false}
    />
  );
};

export default HomeLocationField;
